import { useState, useEffect, useMemo } from "react";
import { II } from "../../components/common/Icons";
import api from "../../services/api";
import HrModal from "./HrModal";
import { useHRData } from "../../context/HRContext";
import HrEIDCardWidget from "./HrEIDCardWidget";
import DocumentVerification from "../../components/common/DocumentVerification";import "../../assets/styles/HR/EmployeesPg.css";


// ── Monthly CSV helpers ──
const _mKey = (d) => {
  if (!d) return null;
  try { const dt = new Date(d); if(isNaN(dt)) return null; return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`; } catch { return null; }
};
const _mLabel = (k) => {
  if (!k) return "";
  const [y, m] = k.split("-");
  return new Date(Number(y), Number(m)-1, 1).toLocaleDateString("en-IN", { month:"long", year:"numeric" });
};
const _fmtFull = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }); } catch { return d; }
};

function exportEmployeesCSV(rows, filename) {
  const headers = ["Emp ID","Name","Email","Department","Designation","Join Date","Manager","Employment Type","Status","Added On"];
  const lines = [headers.join(",")];
  rows.forEach(r => {
    lines.push([
      r.emp_id||r.id, r.name, r.email||"",
      r.department||r.dept||"",
      r.designation||r.role||"",
      r.joinDate ? _fmtFull(r.joinDate) : (r.joined||"—"),
      r.manager||"",
      r.employmentType||"Full-time",
      r.status||"",
      r.user_created_at ? _fmtFull(r.user_created_at) : "—"
    ].map(v=>`"${String(v||"").replace(/"/g,'""')}"`).join(","));
  });
  const blob = new Blob([lines.join("\n")],{type:"text/csv;charset=utf-8;"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

function EmployeesPg() {
  const { hrEmployees, refreshEmployees } = useHRData();
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCredModal, setShowCredModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [eidPreviewId, setEidPreviewId] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null);
  const [docReviewState, setDocReviewState] = useState({});
  const [rejectDocModal, setRejectDocModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [resendModal, setResendModal] = useState(null);
  const [resendReason, setResendReason] = useState("");
  const [resendSections, setResendSections] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [monthFilter, setMonthFilter] = useState("all");
  const [showMonthDrop, setShowMonthDrop] = useState(false);
  const [search, setSearch] = useState("");
  const [managerModal, setManagerModal] = useState(null);
  const [selectedManagerId, setSelectedManagerId] = useState("");
  const [designationModal, setDesignationModal] = useState(null);
  const [newDesignation, setNewDesignation] = useState("");
  const [designationSaving, setDesignationSaving] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    department: "Engineering",
    designation: "Software Engineer",
    joinDate: "",
    manager: "",
    employmentType: "Full-time"
  });

  // Credentials state
  const [credentials, setCredentials] = useState({
    tempId: "",
    tempPw: "",
    permId: "",
    permPw: ""
  });

  const [editCred, setEditCred] = useState({
    id: "",
    pw: ""
  });

  const shareTempCredentials = async () => {
    if (!credentials.tempId || !credentials.tempPw) return;
    try {
      setLoading(true);
      await api.createCredentials(credentials.tempId, credentials.tempPw);
      setSuccessMsg("Temporary credentials shared to employee email.");
      setShowAddModal(false);
      setCredentials({ tempId: "", tempPw: "", permId: "", permPw: "" });
      setTimeout(() => setSuccessMsg(""), 5000);
    } catch (e) {
      alert("Share failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Reporting Manager dropdown — fetch PMs from DB
  const [pmList, setPmList] = useState([]);
  useEffect(() => {
    api.getPMs().then((data) => setPmList(data || [])).catch(() => {});
  }, []);

  // Load employees from API
  useEffect(() => {
    const timer = setTimeout(() => fetchEmployees(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (hrEmployees && hrEmployees.length) {
      setEmployeeList(hrEmployees);
    }
  }, [hrEmployees]);

  const fetchEmployees = async (q = "") => {
    try {
      setLoading(true);
      const data = await api.getHrEmployees(q);
      setEmployeeList(data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // CREATE EMPLOYEE (Temporary Login)
  // =========================================
  const createEmployee = async () => {
    if (!formData.name || !formData.email) {
      alert("Name and Email are required!");
      return;
    }

    try {
      setLoading(true);
      const res = await api.createEmployee({
        name: formData.name,
        email: formData.email,
        department: formData.department,
        designation: formData.designation,
        joinDate: formData.joinDate || new Date().toISOString().split('T')[0],
        manager: formData.manager,
        employmentType: formData.employmentType
      });

      // Add new employee to list
      const newEmployee = {
        id: res.employee?.tempId || res.employee?.id,
        name: formData.name,
        email: formData.email,
        department: formData.department,
        designation: formData.designation,
        status: "onboarding",
        joined: formData.joinDate ? new Date(formData.joinDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        employmentType: formData.employmentType,
        manager: formData.manager
      };

      setEmployeeList([newEmployee, ...employeeList]);

      // Show temporary credentials
      setCredentials({
        tempId: res.credentials?.id || res.employee?.tempId,
        tempPw: res.credentials?.password || "Temp@123",
        permId: "",
        permPw: ""
      });
      // Store new emp ID for EID card preview
      setEidPreviewId(res.credentials?.id || res.employee?.id || res.employee?.tempId || null);

      setShowAddModal(true);
      setSuccessMsg("Employee created with temporary login!");

      // Reset form
      setFormData({
        name: "", email: "", department: "Engineering", designation: "Software Engineer",
        joinDate: "", manager: "", employmentType: "Full-time"
      });

      setTimeout(() => setSuccessMsg(""), 4000);

      // Refresh data
      if (refreshEmployees) await refreshEmployees();
      await fetchEmployees();

    } catch (error) {
      console.error("Create employee error:", error);
      alert("Error creating employee: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // VERIFY EMPLOYEE (Approve/Reject)
  // =========================================
  const verifyEmployee = async (id, action, reason = "") => {
    try {
      setLoading(true);
      const res = await api.verifyEmployee(id, action, reason);

      const matchId = (emp) => emp.emp_id === id || emp.id === id || String(emp.id) === String(id);

      if (action === "approve") {
        const newPermId = res.permId || `EMP-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(4, '0')}`;
        const newPermPw = res.permPw || "Welcome@123";
        // Update employee list with verified status and permanent ID
        setEmployeeList(displayList.map((emp) =>
        matchId(emp) ?
        {
          ...emp,
          id: res.permId || emp.emp_id || emp.id,
          emp_id: res.permId || emp.emp_id || emp.id,
          status: "active",
          verifiedAt: new Date().toISOString()
        } :
        emp
        ));

        // Show permanent credentials
        setCredentials({
          tempId: id,
          tempPw: "",
          permId: newPermId,
          permPw: newPermPw
        });
        setEditCred({ id: newPermId, pw: newPermPw });

        setShowCredModal(true);
        setSuccessMsg(`Employee verified! Permanent ID: ${newPermId}`);
      } else {
        // Reject - update status
        setEmployeeList(employeeList.map((emp) =>
        matchId(emp) ? { ...emp, status: "rejected" } : emp
        ));
        setSuccessMsg("Employee verification rejected.");
      }

      setSelectedEmployee(null);
      setTimeout(() => setSuccessMsg(""), 6000);

      // Refresh data
      if (refreshEmployees) await refreshEmployees();
      await fetchEmployees();

    } catch (error) {
      console.error("Verify employee error:", error);
      alert("Error: " + (error.message || "Failed to verify employee"));
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // VIEW ONBOARDING SUBMISSION
  // =========================================
  const viewOnboarding = async (employeeId) => {
    try {
      setLoading(true);
      const data = await api.getOnboardingStatus(employeeId);
      setOnboardingData(data);
      setShowOnboardingModal(true);
    } catch (error) {
      console.error("Failed to fetch onboarding:", error);
      alert("Could not load onboarding details");
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // UPDATE CREDENTIALS
  // =========================================
  const updateCredentials = async () => {
    const finalId = String(editCred.id || credentials.permId || "").trim();
    const finalPw = String(editCred.pw || credentials.permPw || "").trim();
    if (!finalId || !finalPw) {
      alert("Employee ID and password are required.");
      return;
    }

    try {
      setLoading(true);
      await api.updateCredentials(finalId, finalPw);

      // Update employee list with new ID
      setEmployeeList((prev) => prev.map((emp) =>
      emp.id === credentials.permId ? { ...emp, id: finalId } : emp
      ));

      setCredentials({ ...credentials, permId: finalId, permPw: finalPw });
      setShowCredModal(false);
      setSuccessMsg("Credential sent successfully.");
      alert("Credential sent successfully.");
      setTimeout(() => setSuccessMsg(""), 4000);

    } catch (error) {
      console.error("Update credentials error:", error);
      alert("Error: " + (error.message || "Failed to update credentials"));
    } finally {
      setLoading(false);
    }
  };

  const editEmployeeEmail = async (emp) => {
    const current = emp.email || '';
    const next = window.prompt('Update employee email', current);
    if (!next || !next.trim() || next.trim() === current) return;
    try {
      setLoading(true);
      await api.updateHrUserEmail({ userId: emp.user_id || emp.userId || emp.id, empId: emp.emp_id || emp.id, email: next.trim() });
      setEmployeeList((prev) => prev.map((e) =>
      (e.user_id || e.userId || e.id) === (emp.user_id || emp.userId || emp.id) ? { ...e, email: next.trim() } : e
      ));
      setSuccessMsg('Employee email updated');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (e) {
      alert('Email update failed: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const openManagerModal = (emp) => {
    const currentManagerName = String(emp.manager || emp.manager_name || '').trim().toLowerCase();
    const defaultManager = pmList.find((pm) => String(pm.name || '').trim().toLowerCase() === currentManagerName);
    setManagerModal(emp);
    setSelectedManagerId(defaultManager ? String(defaultManager.id) : "");
  };

  const assignManager = async () => {
    if (!managerModal) return;
    if (!selectedManagerId) {
      alert("Please select a manager.");
      return;
    }
    try {
      setLoading(true);
      const chosenManager = pmList.find((pm) => String(pm.id) === String(selectedManagerId));
      const payload = {
        userId: managerModal.user_id || managerModal.userId || managerModal.id,
        empId: managerModal.emp_id || managerModal.id,
        managerId: Number(selectedManagerId),
        managerRef: selectedManagerId
      };
      await api.updateHrUserManager(payload);
      const managerName = chosenManager?.name || "";
      setEmployeeList((prev) => prev.map((e) =>
      (e.user_id || e.userId || e.id) === (managerModal.user_id || managerModal.userId || managerModal.id) ? { ...e, manager: managerName, manager_name: managerName } : e
      ));
      setSelectedEmployee((prev) =>
      prev && (prev.user_id || prev.userId || prev.id) === (managerModal.user_id || managerModal.userId || managerModal.id) ? { ...prev, manager: managerName, manager_name: managerName } : prev
      );
      setSuccessMsg("Manager assigned successfully.");
      setManagerModal(null);
      setSelectedManagerId("");
      if (refreshEmployees) await refreshEmployees();
      await fetchEmployees(search);
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (e) {
      alert("Manager assign failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const saveDesignation = async () => {
    if (!designationModal) return;
    const desig = newDesignation.trim();
    if (!desig) { alert("Designation cannot be empty."); return; }
    try {
      setDesignationSaving(true);
      await api.updateHrUserDesignation({
        userId: designationModal.user_id || designationModal.userId || designationModal.id,
        empId:  designationModal.emp_id  || designationModal.id,
        designation: desig,
      });
      // Update local list & selected employee
      setEmployeeList((prev) => prev.map((e) =>
        (e.user_id || e.userId || e.id) === (designationModal.user_id || designationModal.userId || designationModal.id)
          ? { ...e, designation: desig }
          : e
      ));
      setSelectedEmployee((prev) =>
        prev && (prev.user_id || prev.userId || prev.id) === (designationModal.user_id || designationModal.userId || designationModal.id)
          ? { ...prev, designation: desig }
          : prev
      );
      setSuccessMsg(`Designation updated to "${desig}" for ${designationModal.name || ""}.`);
      setDesignationModal(null);
      setNewDesignation("");
      if (refreshEmployees) await refreshEmployees();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (e) {
      alert("Designation update failed: " + e.message);
    } finally {
      setDesignationSaving(false);
    }
  };

  // =========================================
  // RESEND ONBOARDING LINK
  // =========================================
  const resendOnboarding = async (empId) => {
    setResendModal(empId);
    setResendReason("");
    setResendSections([]);
  };

  // Document actions
  const viewDocument = async (docId) => {
    try {
      await api.previewDocById(docId);
    } catch (e) {alert("Could not load document: " + e.message);}
  };

  const downloadDocument = async (docId, docName) => {
    try {
      const data = await api.getDocUrl(docId);
      if (data.url) {
        await api.downloadFile(data.url, docName || "document");
      }
    } catch (e) {alert("Download failed: " + e.message);}
  };

  const reviewDocument = async (docId, status, comment = "") => {
    try {
      await api.reviewDoc(docId, status, comment);
      // Refresh onboarding data
      if (onboardingData?.user?.emp_id) {
        const data = await api.getOnboardingStatus(onboardingData.user.emp_id);
        setOnboardingData(data);
      }
      setRejectDocModal(null);
      setRejectReason("");
    } catch (e) {alert("Review failed: " + e.message);}
  };

  const resendWithReason = async () => {
    if (!resendModal) return;
    if (!resendSections.length) {
      alert("Please select at least one section to resend.");
      return;
    }
    try {
      await api.requestResubmission(resendModal, resendReason, resendSections);
      setSuccessMsg("Onboarding resent with reason!");
      setResendModal(null);
      setResendReason("");
      setResendSections([]);
      setShowOnboardingModal(false);
      refreshEmployees?.();
      fetchEmployees();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (e) {alert("Resend failed: " + e.message);}
  };

  const docTypeLabels = {
    aadhaar: "Aadhaar Card", pan: "PAN Card", photo: "Passport Photo",
    driving_license: "Driving License", passport: "Passport",
    marksheet_10: "10th Marksheet", marksheet_12: "12th Marksheet",
    college_id: "College ID", other: "Other Document"
  };

  const docStatusColors = {
    available: { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", label: "Uploaded" },
    pending: { bg: "#fff7ed", border: "#fed7aa", text: "#9a3412", label: "Pending Review" },
    rejected: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b", label: "Rejected" },
    approved: { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", label: "Approved" }
  };

  // =========================================
  // UI Helpers
  // =========================================
  const getStatusClass = (status) => {
    switch (status) {
      case "active":return "chip-ok";
      case "verified":return "chip-ok";
      case "rejected":return "chip-dn";
      case "onboarding":return "chip-ip";
      case "submitted":return "chip-ip";
      case "exiting":return "chip-wn";
      default:return "chip-wn";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "active":return "active";
      case "verified":return "active";
      case "rejected":return "rejected";
      case "onboarding":return "onboarding";
      case "submitted":return "submitted";
      case "exiting":return "exiting";
      default:return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '—' || dateString === '-') return "—";
    if (/^[A-Z][a-z]{2}\s\d{4}$/.test(dateString)) return dateString;
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return dateString;
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {return dateString;}
  };

  // ── Monthly derived data ──
  const availMonths = useMemo(() => {
    const set = new Set();
    employeeList.forEach(r => {
      const k = _mKey(r.user_created_at || r.joinDate);
      if (k) set.add(k);
    });
    return ["all", ...Array.from(set).sort((a,b) => b.localeCompare(a))];
  }, [employeeList]);

  const displayList = useMemo(() => {
    if (monthFilter === "all") return employeeList;
    return employeeList.filter(r => _mKey(r.user_created_at || r.joinDate) === monthFilter);
  }, [employeeList, monthFilter]);

  return (
    <div>
      {/* Header */}
      <div className="employeespg-inline-1">
        <div className="employeespg-inline-2">
          <div className="sec employeespg-inline-3">Employee Management</div>
          <div className="employeespg-inline-4">
            {II("search", 14, { style: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", opacity: 0.5 } })}
            <input
              className="fi employeespg-inline-5"
              placeholder="Search by name, ID or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>

          {/* ── Month select ── */}
          <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
            style={{ padding:"7px 10px", border:"1px solid #e2e8f0", borderRadius:8,
              fontSize:12, color:"#374151", background:"#fff", cursor:"pointer" }}>
            <option value="all">All Months</option>
            {availMonths.filter(m=>m!=="all").map(m=>(
              <option key={m} value={m}>{_mLabel(m)}</option>
            ))}
          </select>

          {/* ── Monthly CSV dropdown ── */}
          <div style={{ position:"relative" }}>
            <button onClick={() => setShowMonthDrop(v=>!v)}
              style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 12px",
                background:"#fff", border:"1px solid #e2e8f0", borderRadius:8,
                fontWeight:600, fontSize:12, color:"#374151", cursor:"pointer" }}>
              📅 Monthly CSV ▾
            </button>
            {showMonthDrop && (
              <div style={{ position:"absolute", right:0, top:"calc(100% + 6px)", background:"#fff",
                border:"1px solid #e2e8f0", borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,.14)",
                zIndex:300, minWidth:240, overflow:"hidden" }}>
                <div style={{ padding:"8px 12px", fontSize:11, fontWeight:700, color:"#64748b",
                  borderBottom:"1px solid #f1f5f9", background:"#f8fafc" }}>
                  MONTH-WISE DOWNLOAD
                </div>
                {availMonths.filter(m=>m!=="all").length === 0 ? (
                  <div style={{ padding:"12px", fontSize:12, color:"#94a3b8" }}>No data yet</div>
                ) : availMonths.filter(m=>m!=="all").map(m => {
                  const mRows = employeeList.filter(r => _mKey(r.user_created_at||r.joinDate) === m);
                  return (
                    <button key={m}
                      onClick={() => { exportEmployeesCSV(mRows, `employees_${m}.csv`); setShowMonthDrop(false); }}
                      style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                        width:"100%", padding:"10px 14px", border:"none", background:"#fff",
                        cursor:"pointer", fontSize:13, borderBottom:"1px solid #f8fafc",
                        textAlign:"left" }}
                      onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                      onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                      <span style={{ fontWeight:600, color:"#1e293b" }}>{_mLabel(m)}</span>
                      <span style={{ fontSize:11, color:"#94a3b8" }}>{mRows.length} employees</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Download current view ── */}
          <button onClick={() => exportEmployeesCSV(displayList, `employees${monthFilter!=="all"?"_"+monthFilter:""}.csv`)}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px",
              background:"#0f172a", color:"#fff", border:"none", borderRadius:8,
              fontWeight:700, fontSize:12, cursor:"pointer", whiteSpace:"nowrap" }}>
            ⬇ Download CSV {displayList.length !== employeeList.length ? `(${displayList.length})` : ""}
          </button>

          <button
            className="btn btn-s"
            onClick={() => {
              setShowAddModal(true);
              setCredentials({ tempId: "", tempPw: "", permId: "", permPw: "" });
            }}
            disabled={loading}>
            {II("plus", 14)} Add Employee
          </button>
        </div>
      </div>

      {/* Success Toast */}
      {successMsg &&
      <div className="toast">
          {II("chk", 16, { c: "#2563eb" })} {successMsg}
        </div>
      }

      {/* Loading State */}
      {loading &&
      <div className="employeespg-inline-6">
          <div className="ob-pending-dot employeespg-inline-7"></div>
          Loading...
        </div>
      }

      {/* ── Monthly filter banner ── */}
      {monthFilter !== "all" && (
        <div style={{ background:"linear-gradient(135deg,#1e1b4b,#4f46e5)", borderRadius:10,
          padding:"12px 18px", marginBottom:14, display:"flex", justifyContent:"space-between",
          alignItems:"center", color:"#fff" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:14 }}>📅 {_mLabel(monthFilter)}</div>
            <div style={{ fontSize:11, opacity:.8, marginTop:2 }}>{displayList.length} employees added this month</div>
          </div>
          <div style={{ display:"flex", gap:14 }}>
            {["active","submitted","onboarding","pending","exiting","rejected"].map(s => {
              const ct = displayList.filter(r=>r.status===s).length;
              return ct > 0 ? (
                <div key={s} style={{ textAlign:"center" }}>
                  <div style={{ fontSize:18, fontWeight:800 }}>{ct}</div>
                  <div style={{ fontSize:9, opacity:.75, textTransform:"capitalize" }}>{s}</div>
                </div>
              ) : null;
            })}
          </div>
          <button onClick={() => exportEmployeesCSV(displayList, `employees_${monthFilter}.csv`)}
            style={{ padding:"7px 14px", background:"rgba(255,255,255,.2)", border:"1px solid rgba(255,255,255,.3)",
              borderRadius:8, color:"#fff", fontWeight:700, fontSize:12, cursor:"pointer" }}>
            ⬇ Download Month CSV
          </button>
        </div>
      )}

      {/* Employees Table */}
      <div className="cd">
        <div className="cd-b employeespg-inline-8">
          <table className="tbl">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Department</th>
                <th>Designation</th>
                <th>Joined</th>
                <th>Manager</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayList.length === 0 ?
              <tr>
                  <td colSpan="10" className="employeespg-inline-9">
                    {monthFilter !== "all" ? `No employees added in ${_mLabel(monthFilter)}` : "No employees found. Create your first employee!"}
                  </td>
                </tr> :

              employeeList.map((emp) =>
              <tr key={emp.id}>
                    <td className="employeespg-inline-10">{emp.emp_id || emp.id}</td>
                    <td className="employeespg-inline-11">{emp.name}</td>
                    <td className="employeespg-inline-12">{emp.email || "—"}</td>
                    <td>{emp.department || emp.dept || "—"}</td>
                    <td>{emp.designation || emp.role || "—"}</td>
                    <td>{formatDate(emp.joinDate) || emp.joined || "—"}</td>
                    <td>{emp.manager || "—"}</td>
                    <td>{emp.employmentType || "Full-time"}</td>
                    <td>
                      <span className={`chip ${getStatusClass(emp.status)}`}>
                        {getStatusLabel(emp.status)}
                      </span>
                    </td>
                    <td className="employeespg-actions-cell">
                      <div className="employeespg-inline-13">
                        {/* View Details Button */}
                        <button
                      className="btn-o btn-s employeespg-act-btn"
                      onClick={() => setSelectedEmployee(emp)}
                      title="View Details">
                      
                          {II("eye", 14)}
                        </button>
                        <button
                      className="btn-o btn-s employeespg-act-btn"
                      onClick={() => editEmployeeEmail(emp)}
                      disabled={loading}
                      title="Edit Email">
                      
                          {II("edit", 14)}
                        </button>
                        <button
                      className="btn-o btn-s employeespg-act-btn"
                      onClick={() => openManagerModal(emp)}
                      disabled={loading}
                      title="Assign Manager">

                          {II("people", 14)}
                        </button>
                        <button
                      className="btn-o btn-s employeespg-act-btn"
                      onClick={() => { setDesignationModal(emp); setNewDesignation(emp.designation || ""); }}
                      disabled={loading}
                      title="Reassign Designation">
                          {II("briefcase", 14)}
                        </button>

                        {/* View Onboarding Button - Show for pending/submitted/active */}
                        {["pending", "submitted", "onboarding", "active"].includes(emp.status) &&
                    <button
                      className="btn-o btn-s employeespg-act-btn employeespg-onboarding-btn"
                      onClick={() => viewOnboarding(emp.user_id || emp.userId)}
                      title="View Onboarding">
                      
                            {II("note", 14)}
                          </button>
                    }

                        {/* Approve/Reject: always visible for consistent 6 actions, enabled only for pending/submitted */}
                        <button
                      className="btn btn-s btn-ok employeespg-act-btn"
                      onClick={() => {
                        if (!(emp.status === "pending" || emp.status === "submitted")) {
                          alert("Approve is allowed only for pending/submitted employees");
                          return;
                        }
                        verifyEmployee(emp.emp_id || emp.id, "approve");
                      }}
                      disabled={loading}
                      title="Approve">
                      
                          {II("chk", 14)}
                        </button>
                        <button
                      className="btn btn-s btn-dn employeespg-act-btn"
                      onClick={() => {
                        if (!(emp.status === "pending" || emp.status === "submitted")) {
                          alert("Reject is allowed only for pending/submitted employees");
                          return;
                        }
                        const reason = window.prompt("Enter rejection reason");
                        if (!reason || !reason.trim()) return;
                        verifyEmployee(emp.emp_id || emp.id, "reject", reason.trim());
                      }}
                      disabled={loading}
                      title="Reject">
                      
                          {II("x", 14)}
                        </button>

                        {/* Credentials Button - same flow as interns */}
                        {!["inactive", "deleted", "completed", "rejected"].includes(String(emp.status || "").toLowerCase()) &&
                    <button
                      className="btn btn-s employeespg-inline-14 employeespg-act-btn"

                      onClick={() => {
                        setCredentials({
                          ...credentials,
                          permId: emp.emp_id || emp.id,
                          permPw: "••••••••"
                        });
                        setEditCred({ id: emp.emp_id || emp.id, pw: "" });
                        setShowCredModal(true);
                      }}
                      title="View Credentials">
                      
                            {II("key", 14)}
                          </button>
                    }
                        {/* EID Card Preview Button */}
                        {(emp.emp_id || emp.id) &&
                    <button
                      className="btn-o btn-s employeespg-act-btn"
                      onClick={() => setEidPreviewId(emp.emp_id || emp.id)}
                      title="Preview EID Card"
                      style={{ fontSize: 13 }}>
                      🪪
                    </button>
                    }
                      </div>
                    </td>
                  </tr>
              )
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* ========== MODAL: Assign Manager ========== */}
      <HrModal
        open={managerModal !== null}
        onClose={() => {setManagerModal(null);setSelectedManagerId("");}}
        title={`Assign Manager: ${managerModal?.name || ""}`}>

        {managerModal &&
        <div>
            <div className="fg">
              <label className="fl">Select Manager</label>
              <select
                className="fsel"
                value={selectedManagerId}
                onChange={(e) => setSelectedManagerId(e.target.value)}>

                <option value="">Select Manager</option>
                {pmList.map((pm) =>
              <option key={pm.id} value={pm.id}>{pm.name} ({pm.emp_id})</option>
              )}
              </select>
            </div>
            <div className="employeespg-inline-17">
              <button className="btn-o" onClick={() => {setManagerModal(null);setSelectedManagerId("");}}>Cancel</button>
              <button className="btn" onClick={assignManager} disabled={loading || !selectedManagerId}>
                {loading ? "Saving..." : "Submit"}
              </button>
            </div>
          </div>
        }
      </HrModal>

      {/* ========== MODAL: Reassign Designation ========== */}
      <HrModal
        open={designationModal !== null}
        onClose={() => { setDesignationModal(null); setNewDesignation(""); }}
        title={`Reassign Designation: ${designationModal?.name || ""}`}>
        {designationModal &&
          <div>
            <div className="fg">
              <label className="fl">Current Designation</label>
              <div style={{ padding: "6px 10px", background: "#f4f7fc", borderRadius: 4, fontSize: 13, color: "#5b7a9e", marginBottom: 12 }}>
                {designationModal.designation || designationModal.role || "—"}
              </div>
              <label className="fl">New Designation</label>
              <input
                className="fi"
                type="text"
                placeholder="Enter new designation (e.g. Senior Developer)"
                value={newDesignation}
                onChange={(e) => setNewDesignation(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveDesignation(); }}
                autoFocus
              />
            </div>
            <div className="employeespg-inline-17">
              <button className="btn-o" onClick={() => { setDesignationModal(null); setNewDesignation(""); }}>Cancel</button>
              <button className="btn" onClick={saveDesignation} disabled={designationSaving || !newDesignation.trim()}>
                {designationSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        }
      </HrModal>

      {/* ========== MODAL: Employee Details ========== */}
      <HrModal
        open={selectedEmployee !== null}
        onClose={() => setSelectedEmployee(null)}
        title={`Employee: ${selectedEmployee?.name || ""}`}>
        
        {selectedEmployee &&
        <div>
            <div className="g2 mb14">
              {[
            ["Employee ID", selectedEmployee.emp_id || selectedEmployee.id],
            ["Email", selectedEmployee.email || "—"],
            ["Department", selectedEmployee.department || selectedEmployee.dept || "—"],
            ["Designation", selectedEmployee.designation || selectedEmployee.role || "—"],
            ["Join Date", formatDate(selectedEmployee.joinDate) || selectedEmployee.joined || "—"],
            ["Manager", selectedEmployee.manager || "—"],
            ["Employment Type", selectedEmployee.employmentType || "Full-time"],
            ["Status", getStatusLabel(selectedEmployee.status)]].
            map(([label, value], i) =>
            <div key={i}>
                  <div className="employeespg-inline-15">{label}</div>
                  <div className="employeespg-inline-16">{value}</div>
                </div>
            )}
            </div>

            {/* Action Buttons */}
            <div className="employeespg-inline-17">
              {(selectedEmployee.status === "pending" || selectedEmployee.status === "submitted") &&
            <>
                  <button
                className="btn btn-ok"
                onClick={() => verifyEmployee(selectedEmployee.emp_id || selectedEmployee.id, "approve")}
                disabled={loading}>
                
                    {II("chk", 14)} Accept Employee
                  </button>
                  <button
                className="btn btn-dn"
                onClick={() => {
                  const reason = window.prompt("Enter rejection reason");
                  if (!reason || !reason.trim()) return;
                  verifyEmployee(selectedEmployee.emp_id || selectedEmployee.id, "reject", reason.trim());
                }}
                disabled={loading}>
                
                    {II("x", 14)} Reject
                  </button>
                </>
            }
              <button className="btn-o" onClick={() => setSelectedEmployee(null)}>Close</button>
            </div>
          </div>
        }
      </HrModal>

      {/* ========== MODAL: Create Employee ========== */}
      <HrModal
        open={showAddModal && !credentials.permId}
        onClose={() => setShowAddModal(false)}
        title="Create New Employee">
        
        <div>
          <div className="fg">
            <label className="fl">Full Name *</label>
            <input
              className="fi"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter employee full name" />
            
          </div>

          <div className="fg">
            <label className="fl">Email *</label>
            <input
              className="fi"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="employee@company.com" />
            
          </div>

          <div className="fr2">
            <div className="fg">
              <label className="fl">Department</label>
              <select
                className="fsel"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}>
                
                <option>Engineering</option>
                <option>Design</option>
                <option>Marketing</option>
                <option>HR</option>
                <option>Sales</option>
                <option>Finance</option>
                <option>Product</option>
                <option>Operations</option>
              </select>
            </div>

            <div className="fg">
              <label className="fl">Designation</label>
              <input
                className="fi"
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                placeholder="e.g. Software Engineer" />
              
            </div>
          </div>

          <div className="fr2">
            <div className="fg">
              <label className="fl">Join Date</label>
              <input
                className="fi"
                type="date"
                value={formData.joinDate}
                onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })} />
              
            </div>

            <div className="fg">
              <label className="fl">Employment Type</label>
              <select
                className="fsel"
                value={formData.employmentType}
                onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}>
                
                <option>Full-time</option>
                <option>Part-time</option>
                <option>Contract</option>
                <option>Probation</option>
              </select>
            </div>
          </div>

          <div className="fg">
            <label className="fl">Reporting Manager</label>
            <select
              className="fsel"
              value={formData.manager}
              onChange={(e) => setFormData({ ...formData, manager: e.target.value })}>
              
              <option value="">Select Manager</option>
              {pmList.map((pm) =>
              <option key={pm.id} value={pm.name}>{pm.name} ({pm.emp_id})</option>
              )}
            </select>
          </div>

          <div className="employeespg-inline-18">
            <button className="btn-o" onClick={() => setShowAddModal(false)}>Cancel</button>
            <button className="btn" onClick={createEmployee} disabled={loading}>
              {loading ? "Creating..." : "Create Employee"}
            </button>
          </div>
        </div>
      </HrModal>

      {/* ========== MODAL: Temporary Credentials ========== */}
      <HrModal
        open={showAddModal && credentials.tempId}
        onClose={() => setShowAddModal(false)}
        title="Temporary Login Created">
        
        <div>
          <div className="employeespg-inline-19">





            
            <div className="employeespg-inline-20">
              ✅ Temporary Login Created!
            </div>

            <div className="employeespg-inline-21">
              <span className="employeespg-inline-22">Employee has been created. Share these credentials:</span>
            </div>

            <div className="employeespg-inline-23">





              
              <div className="employeespg-inline-24">
                🔐 TEMPORARY LOGIN DETAILS
              </div>

              <div className="employeespg-inline-25">








                
                {credentials.tempId}
              </div>

              <div className="employeespg-inline-26">







                
                {credentials.tempPw}
              </div>

              <div className="employeespg-inline-27">
                Employee will use these to log in and complete onboarding.
              </div>
            </div>
          </div>

          <button
            className="btn employeespg-inline-28"
            onClick={shareTempCredentials}
            disabled={loading}>

            
            {loading ? "Sharing..." : "Share"}
          </button>
          {eidPreviewId && (
            <button
              className="btn-o"
              style={{ marginTop: 8, width: "100%" }}
              onClick={() => { setShowAddModal(false); setTimeout(() => setEidPreviewId(eidPreviewId), 100); }}>
              🪪 Preview EID Card
            </button>
          )}
        </div>
      </HrModal>

      {/* ========== MODAL: Permanent Credentials ========== */}
      <HrModal
        open={showCredModal}
        onClose={() => setShowCredModal(false)}
        title="Permanent Employee Credentials">
        
        <div>
          <div className="employeespg-inline-29">




            
            <div className="employeespg-inline-30">
              {II("key", 18, { c: "#4338ca" })} Permanent Login Details
            </div>

            <div className="fg">
              <label className="fl">Employee ID (Permanent)</label>
              <input
                className="fi employeespg-inline-31"
                value={editCred.id || credentials.permId}
                onChange={(e) => setEditCred({ ...editCred, id: e.target.value })} />

              
            </div>

            <div className="fg">
              <label className="fl">Password</label>
              <input
                className="fi employeespg-inline-32"
                type="text"
                value={editCred.pw || credentials.permPw}
                onChange={(e) => setEditCred({ ...editCred, pw: e.target.value })} />

              
            </div>

            <div className="employeespg-inline-33">
              You can edit these credentials before saving. Share these with the employee.
            </div>
          </div>

          <div className="employeespg-inline-34">
            <button className="btn-o" onClick={() => setShowCredModal(false)}>Cancel</button>
            <button className="btn" onClick={updateCredentials} disabled={loading}>
              {loading ? "Saving..." : "Save & Share"}
            </button>
          </div>
        </div>
      </HrModal>

      {/* ========== MODAL: Onboarding Submission ========== */}
      <HrModal
        open={showOnboardingModal}
        onClose={() => setShowOnboardingModal(false)}
        title="Onboarding Submission Details">
        
        {onboardingData &&
        <div>
            <div className="employeespg-inline-35">
              <div className="employeespg-inline-36">
                <span className="employeespg-inline-37">Submitted:</span>
                <span className="employeespg-inline-38">
                  {formatDate(onboardingData.onboarding?.submitted_at)}
                </span>
              </div>
            </div>

            <div className="employeespg-inline-39">
              {/* Personal Details */}
              <div className="employeespg-inline-40">
                <div className="employeespg-inline-41">
                  👤 Personal Details
                </div>
                <div className="employeespg-inline-42">
                  {[
                ["Full Name", onboardingData.profile?.full_name],
                ["DOB", onboardingData.profile?.dob],
                ["Blood Group", onboardingData.profile?.blood_group],
                ["Gender", onboardingData.profile?.gender],
                ["Phone", onboardingData.profile?.phone],
                ["Email", onboardingData.profile?.personal_email],
                ["PAN", onboardingData.profile?.pan_number]].
                map(([label, value], i) =>
                <div key={i}>
                      <div className="employeespg-inline-43">{label}</div>
                      <div className="employeespg-inline-44">{value || "—"}</div>
                    </div>
                )}
                </div>
              </div>

              {/* Address */}
              <div className="employeespg-inline-45">
                <div className="employeespg-inline-46">
                  🏠 Address
                </div>
                <div className="employeespg-inline-47">
                  <div>
                    <div className="employeespg-inline-48">Current Address</div>
                    <div className="employeespg-inline-49">
                      {[
                    onboardingData.profile?.current_address,
                    onboardingData.profile?.current_city,
                    onboardingData.profile?.current_state,
                    onboardingData.profile?.current_pincode].
                    filter(Boolean).join(", ") || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="employeespg-inline-50">Permanent Address</div>
                    <div className="employeespg-inline-51">
                      {[
                    onboardingData.profile?.permanent_address,
                    onboardingData.profile?.permanent_city,
                    onboardingData.profile?.permanent_state,
                    onboardingData.profile?.permanent_pincode].
                    filter(Boolean).join(", ") || "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="employeespg-inline-52">
                <div className="employeespg-inline-53">
                  🏦 Bank Details
                </div>
                <div className="employeespg-inline-54">
                  {[
                ["Bank Name", onboardingData.bank_details?.bank_name],
                ["Account No", onboardingData.bank_details?.account_number],
                ["IFSC Code", onboardingData.bank_details?.ifsc_code],
                ["Branch", onboardingData.bank_details?.branch]].
                map(([label, value], i) =>
                <div key={i}>
                      <div className="employeespg-inline-55">{label}</div>
                      <div className="employeespg-inline-56">{value || "—"}</div>
                    </div>
                )}
                </div>
              </div>

              {/* Education */}
              {onboardingData.education && onboardingData.education.length > 0 &&
            <div>
                  <div className="employeespg-inline-57">
                    🎓 Education
                  </div>
                  {onboardingData.education.map((edu, idx) =>
              <div key={idx} className="employeespg-inline-58">
                      <div className="employeespg-inline-59">{edu.degree} {edu.specialization && `- ${edu.specialization}`}</div>
                      <div className="employeespg-inline-60">{edu.institution}</div>
                      <div className="employeespg-inline-61">
                        <span className="employeespg-inline-62">
                          {edu.start_year} - {edu.is_pursuing ? "Present" : edu.end_year}
                        </span>
                        <span className="employeespg-inline-63">
                          {edu.score} {edu.score_type}
                        </span>
                      </div>
                    </div>
              )}
                </div>
            }
            </div>

            {/* ═══════ DOCUMENTS VERIFICATION ═══════ */}
            <DocumentVerification
            documents={onboardingData.documents || []}
            canUpload={true}
            targetUserId={onboardingData.user?.id || onboardingData.profile?.user_id || selectedEmployee?.user_id || selectedEmployee?.userId || null}
            targetEmpId={onboardingData.user?.emp_id || selectedEmployee?.emp_id || selectedEmployee?.id || null}
            onRefresh={async () => {
              if (onboardingData?.user?.emp_id) {
                const data = await api.getOnboardingStatus(onboardingData.user.emp_id);
                setOnboardingData(data);
              }
            }} />
          

            {/* ═══════ ACTION BUTTONS ═══════ */}
            <div className="employeespg-inline-64">
              <button className="btn-o" onClick={() => setShowOnboardingModal(false)}>Close</button>
              {(onboardingData.status === "ONBOARDING_SUBMITTED" || onboardingData.onboarding?.status === "submitted") &&
            <>
                  <button
                className="btn employeespg-inline-65"

                onClick={() => {
                  setResendModal(selectedEmployee?.emp_id || onboardingData.user?.emp_id);
                  setResendReason("");
                  setResendSections([]);
                }}>
                
                    🔄 Resend with Reason
                  </button>
                  <button
                className="btn btn-ok"
                onClick={() => {
                  const allDocs = onboardingData.documents || [];
                  const rejected = allDocs.filter((d) => d.status === "rejected");
                  if (rejected.length > 0) {
                    alert(`Cannot approve: ${rejected.length} document(s) still rejected. Please review all documents first.`);
                    return;
                  }
                  setShowOnboardingModal(false);
                  verifyEmployee(selectedEmployee?.emp_id || onboardingData.user?.emp_id, "approve");
                }}>
                
                    {II("chk", 14)} Approve All
                  </button>
                </>
            }
            </div>
          </div>
        }
      </HrModal>


      {/* ═══════ RESEND WITH REASON MODAL ═══════ */}
      {resendModal &&
      <div className="employeespg-inline-66">
          <div className="employeespg-inline-67">
            <div className="employeespg-inline-68">
              🔄 Resend Onboarding
            </div>
            <div className="employeespg-inline-69">
              Select which sections need to be resubmitted and provide a reason:
            </div>
            <div className="employeespg-inline-70">
              {["Personal Details", "Address", "Bank Details", "Education", "Documents", "Declaration"].map((s) =>
            <label key={s} style={{ "--employeespg-1-border":

              `1px solid ${resendSections.includes(s) ? "#f59e0b" : "#e2e8f0"}`, "--employeespg-1-background":
              resendSections.includes(s) ? "#fffbeb" : "#fff" }} className="employeespg-dyn-1">

              
                  <input
                type="checkbox"
                checked={resendSections.includes(s)}
                onChange={(e) => {
                  if (e.target.checked) setResendSections((prev) => [...prev, s]);else
                  setResendSections((prev) => prev.filter((x) => x !== s));
                }} className="employeespg-inline-71" />

              
                  {s}
                </label>
            )}
            </div>
            <textarea
            value={resendReason}
            onChange={(e) => setResendReason(e.target.value)}
            placeholder="Reason for resend (visible to employee/intern)..."
            rows={3} className="employeespg-inline-72" />

          
            <div className="employeespg-inline-73">
              <button onClick={() => {setResendModal(null);setResendReason("");setResendSections([]);}} className="employeespg-inline-74">Cancel</button>
              <button
              onClick={resendWithReason}
              disabled={!resendReason.trim()} style={{ "--employeespg-2-cursor":

                resendReason.trim() ? "pointer" : "not-allowed", "--employeespg-2-background":
                resendReason.trim() ? "#f59e0b" : "#fde68a" }} className="employeespg-dyn-2">
              
              
                🔄 Resend to Employee
              </button>
            </div>
          </div>
        </div>
      }

      {/* ========== MODAL: EID Card Preview ========== */}
      <HrModal
        open={!!eidPreviewId}
        onClose={() => setEidPreviewId(null)}
        title="EID Card Preview">
        {eidPreviewId && (
          <div style={{ minWidth: 340 }}>
            <HrEIDCardWidget
              empId={eidPreviewId}
              onClose={() => setEidPreviewId(null)}
            />
          </div>
        )}
      </HrModal>
    </div>);

}

export default EmployeesPg;
